import { FunctionComponent, useEffect, useState } from "react";
import { connect } from "react-redux";
import { Dispatch, RootState } from "core/store/store.types";
import {
  Alert,
  Box,
  Button,
  Grid,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  useTheme,
} from "@mui/material";
import { MetaLogin } from "components/organisms/meta-login";
import { Text } from "components/atoms/text";
import { SocialReportDetail } from "core/types/social.types";
import { socialActions, socialSelectors } from "core/store/modules/social";
import AudienceContent from "containers/audience-content/audience-content";
import { WordCloudContent } from "containers/word-cloud-content";
import { PostsContent } from "containers/posts-content";
import { useTranslation } from "react-i18next";
import { PDFDocument } from "pdf-lib";
import { facebookUserAsync } from "core/store/modules/facebook-user";
import { IgAccountInfo } from "components/molecules/ig-account-info";
import i18n from "i18n";
import { KeyValueInsights } from "components/atoms/key-value-insights";
import { abbreviateNumber } from "core/helpers/abbreviate-number";
import generatePDF, { Resolution, Margin, Options } from "react-to-pdf";
import { GrowthContent } from "containers/growth-content";
import { grey } from "@mui/material/colors";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

interface OwnProps {
  id: number;
  reportDetail?: SocialReportDetail;
}
interface StateProps {
  isReportLoading: boolean;
  reportError?: string;
}
interface DispatchProps {
  resetError: () => void;
  saveFacebookInfo: (userInfo: any) => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const ReportDetail: FunctionComponent<Props> = ({
  id,
  reportDetail,
  isReportLoading,
  reportError,
  resetError,
  saveFacebookInfo,
}) => {
  const { t } = useTranslation();
  const [auth, setAuth] = useState(
    sessionStorage.getItem("Meta_Token") ? true : false
  );
  // const [isAudienceVisible, setIsAudienceVisible] = useState(false);
  const linguaCorrente = i18n.language;
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const followersGrowth = reportDetail?.profileAdvancedInsights?.followersGrowth
    ? reportDetail?.profileAdvancedInsights?.followersGrowth
    : undefined;
  const engagementPosts = reportDetail?.profileAdvancedInsights?.engagementPosts
    ? reportDetail?.profileAdvancedInsights?.engagementPosts
    : undefined;
  const postsFrequency = reportDetail?.profileAdvancedInsights?.postsFrequency
    ? reportDetail?.profileAdvancedInsights?.postsFrequency
    : undefined;
  const valuePostStory = reportDetail?.profileAdvancedInsights?.valuePostStory
    ? reportDetail?.profileAdvancedInsights?.valuePostStory
    : undefined;

  const options: Options = {
    method: "build",
    resolution: Resolution.HIGH,
    page: {
      margin: Margin.SMALL,
      format: "A4",
      orientation: "portrait",
    },
    canvas: {
      mimeType: "image/jpeg",
      qualityRatio: 1,
    },
    overrides: {
      pdf: {
        compress: true,
      },
      canvas: {
        useCORS: true,
      },
    },
  };

  const ReportDetailComponent = () => (
    <div>
      <div id={"report-standard-analytics"}>
        {reportDetail && reportDetail.accountInfo.id && (
          <IgAccountInfo profile={reportDetail.accountInfo} />
        )}
        <Text
          fontSize={"24px"}
          fontWeight={"700"}
          textAlign={"center"}
          mt={"30px"}
        >
          Instagram report
        </Text>
        {reportDetail && (
          <Text variant="body2" textAlign={"center"}>
            {new Date(reportDetail?.timestampAnalysis).toLocaleDateString(
              linguaCorrente === "en" ? "en-GB" : "it-IT",
              { year: "numeric", month: "short", day: "numeric" }
            )}
          </Text>
        )}
        {reportDetail && (
          <Grid container mt={"5px"}>
            {reportDetail.accountInfo.followersCount && (
              <Grid
                item
                xs={12}
                sm={6}
                p={7}
                sx={{
                  border: `0.25px solid ${theme.palette.divider}`,
                  borderLeft: "0px",
                }}
              >
                <KeyValueInsights
                  title={t("social-dashboard.account.followers")}
                  content={abbreviateNumber(
                    reportDetail.accountInfo.followersCount
                  )}
                />
              </Grid>
            )}
            <Grid
              item
              xs={12}
              sm={6}
              p={7}
              sx={{
                border: `0.25px solid ${theme.palette.divider}`,
                borderLeft: "0px",
                borderRight: "0px",
              }}
            >
              <KeyValueInsights
                title={t("social-report-detail.followers-growth")}
                content={
                  followersGrowth
                    ? followersGrowth?.values.growth.value
                    : t("empty_states.empty_stats.title")
                }
                littleText={
                  followersGrowth ? t("social-report-detail.30-days") : ""
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              p={7}
              sx={{
                border: `0.25px solid ${theme.palette.divider}`,
                borderLeft: "0px",
              }}
            >
              <KeyValueInsights
                title={t("social-report-detail.engagement-rate")}
                content={
                  engagementPosts &&
                  engagementPosts?.values?.engagementRateAvg?.value
                    ? engagementPosts.values.engagementRateAvg.value + "%"
                    : t("empty_states.empty_stats.title")
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              p={7}
              sx={{
                border: `0.25px solid ${theme.palette.divider}`,
                borderLeft: "0px",
                borderRight: "0px",
              }}
            >
              <KeyValueInsights
                title={t("social-report-detail.post-frequency")}
                content={
                  postsFrequency
                    ? postsFrequency.value + t("social-report-detail.at-week")
                    : t("empty_states.empty_stats.title")
                }
              />
            </Grid>
          </Grid>
        )}
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} id={"report-audience"}>
          <AudienceContent
            isReportLoading={isReportLoading}
            reportDetail={reportDetail}
          />
        </Grid>
        <Grid item xs={12} id={"report-growth"}>
          <GrowthContent
            isReportLoading={isReportLoading}
            reportDetail={reportDetail}
          />
        </Grid>
        <Grid item xs={12} id={"report-engagement"}>
          <Text fontSize={"18px"} fontWeight={"600"} pb={"15px"}>
            {t("social-report-detail.engagement.title")}
          </Text>
          <Grid container spacing={5}>
            <Grid item>
              <KeyValueInsights
                title={t("social-report-detail.content.avg_like")}
                content={
                  engagementPosts && engagementPosts?.values?.likesAvg?.value
                    ? engagementPosts.values.likesAvg.value
                    : t("empty_states.empty_stats.title")
                }
                size="small"
              />
            </Grid>
            <Grid item>
              <KeyValueInsights
                title={t("social-report-detail.content.avg_comments")}
                content={
                  engagementPosts && engagementPosts?.values?.commentsAvg?.value
                    ? engagementPosts.values.commentsAvg.value
                    : t("empty_states.empty_stats.title")
                }
                size="small"
              />
            </Grid>
            <Grid item>
              <KeyValueInsights
                title={t("social-report-detail.engagement.rate")}
                content={
                  engagementPosts &&
                  engagementPosts?.values?.engagementRateAvg?.value
                    ? engagementPosts.values.engagementRateAvg.value + "%"
                    : t("empty_states.empty_stats.title")
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <Text
                padding={"30px 0px 20px 0px"}
                fontWeight={700}
                style={{
                  color: grey[700],
                }}
                fontSize={"12px"}
              >
                {t("social-report-detail.engagement.subtitle")}
              </Text>
              <Table>
                <TableHead>
                  <TableCell></TableCell>
                  <TableCell>
                    <div
                      style={{
                        display: "flex",
                      }}
                    >
                      <Text fontSize={"12px"} fontWeight={700}>
                        {t("social-report-detail.engagement.rate_previous")}
                      </Text>
                      {engagementPosts &&
                        engagementPosts.values.engagementRatePrevious
                          .description && (
                          <Tooltip
                            title={
                              engagementPosts.values.engagementRatePrevious
                                .description
                            }
                            sx={{ marginLeft: "10px" }}
                          >
                            <InfoOutlinedIcon fontSize="inherit" />
                          </Tooltip>
                        )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Text fontSize={"12px"} fontWeight={700}>
                      {t("social-report-detail.engagement.rate")}
                    </Text>
                  </TableCell>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>
                      {engagementPosts &&
                      engagementPosts.values.engagementRatePrevious.value
                        ? engagementPosts.values.engagementRatePrevious.value +
                          "%"
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {engagementPosts &&
                      engagementPosts.values.engagementRate.value
                        ? engagementPosts.values.engagementRate.value + "%"
                        : "-"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Text fontSize={"12px"} fontWeight={500}>
                        {t("social-report-detail.engagement.avg")}
                      </Text>
                    </TableCell>
                    <TableCell>
                      {engagementPosts &&
                      engagementPosts.values.engagementRateAvgPrevious.value
                        ? engagementPosts.values.engagementRateAvgPrevious
                            .value + "%"
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {engagementPosts &&
                      engagementPosts.values.engagementRateAvg.value
                        ? engagementPosts.values.engagementRateAvg.value + "%"
                        : "-"}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text
                padding={"30px 0px 20px 0px"}
                fontWeight={700}
                style={{
                  color: grey[700],
                }}
                fontSize={"12px"}
              >
                {t("social-report-detail.engagement.like_comment_ratio")}
              </Text>
              <KeyValueInsights
                content={
                  engagementPosts &&
                  engagementPosts.values.likeCommentRatio100.value
                    ? engagementPosts.values.likeCommentRatio100.value
                    : t("empty_states.empty_stats.title")
                }
                littleText={
                  engagementPosts &&
                  engagementPosts.values.likeCommentRatio100.description
                    ? engagementPosts.values.likeCommentRatio100.description
                    : ""
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text
                padding={"30px 0px 20px 0px"}
                fontWeight={700}
                style={{
                  color: grey[700],
                }}
                fontSize={"12px"}
              >
                {t("social-report-detail.engagement.like_spread")}
              </Text>
              <KeyValueInsights
                content={
                  engagementPosts && engagementPosts.values.likesSpread.value
                    ? engagementPosts.values.likesSpread.value + "%"
                    : t("empty_states.empty_stats.title")
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text
                padding={"30px 0px 20px 0px"}
                fontWeight={700}
                style={{
                  color: grey[700],
                }}
                fontSize={"12px"}
              >
                {t("social-report-detail.engagement.est_price")}
              </Text>
              {valuePostStory && (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "20px",
                    }}
                  >
                    <Text fontSize={"11px"}>
                      {valuePostStory?.values.post.value}
                    </Text>
                    <Tooltip
                      title={valuePostStory?.values.post.description}
                      sx={{ marginLeft: "5px" }}
                    >
                      <InfoOutlinedIcon fontSize="inherit" />
                    </Tooltip>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "20px",
                    }}
                  >
                    <Text fontSize={"11px"}>
                      {valuePostStory.values.story.value}
                    </Text>
                    <Tooltip
                      title={valuePostStory.values.story.description}
                      sx={{ marginLeft: "5px" }}
                    >
                      <InfoOutlinedIcon fontSize="inherit" />
                    </Tooltip>
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} id={"report-posts"}>
          <PostsContent
            isReportLoading={isReportLoading}
            posts={reportDetail?.posts}
            averageInteractions={
              engagementPosts && engagementPosts.values.interactionsAvg.value
                ? engagementPosts.values.interactionsAvg.value
                : undefined
            }
            averageReach={
              engagementPosts && engagementPosts.values.reachAvg.value
                ? engagementPosts.values.reachAvg.value
                : undefined
            }
            averageView={
              engagementPosts && engagementPosts.values.impressionsAvg.value
                ? engagementPosts.values.impressionsAvg.value
                : undefined
            }
            engagementAccount={
              reportDetail?.profileAdvancedInsights.engagementAccount
            }
          />
        </Grid>
        <Grid item xs={12} id={"report-word-cloud"}>
          {reportDetail?.wordCloud.length !== 0 && (
            <WordCloudContent
              isReportLoading={isReportLoading}
              wordCloud={reportDetail?.wordCloud}
            />
          )}
        </Grid>
      </Grid>
    </div>
  );

  const generatePDFCustom = async () => {
    //cover
    const coverPDFUrl = `${process.env.PUBLIC_URL}/pdf/cover.pdf`;
    const coverPDFBytes = await fetch(coverPDFUrl).then((res) => {
      if (!res.ok) {
        throw new Error(`Could not fetch the cover PDF, status: ${res.status}`);
      }
      return res.arrayBuffer();
    });
    const coverPdf = await PDFDocument.load(coverPDFBytes);

    //report-builder
    //account-info
    // const getAccountInfoElement = () =>
    //   document.getElementById("report-account-info");
    // const jsPDFAccountInfo = await generatePDF(getAccountInfoElement, options);
    // let jsPDFBytesAccountInfo = jsPDFAccountInfo.output("arraybuffer");
    // const pdfAccountInfo = await PDFDocument.load(jsPDFBytesAccountInfo);

    //report-standard-analytics
    const getStandardAnalyticsElement = () =>
      document.getElementById("report-standard-analytics");
    const jsPDFStandardAnalytics = await generatePDF(
      getStandardAnalyticsElement,
      options
    );
    let jsPDFBytesStandardAnalytics = undefined;
    let pdfStandardAnalytics = undefined;
    if (jsPDFStandardAnalytics) {
      jsPDFBytesStandardAnalytics =
        jsPDFStandardAnalytics.output("arraybuffer");
      pdfStandardAnalytics = await PDFDocument.load(
        jsPDFBytesStandardAnalytics
      );
    }

    //report-audience
    const getReportAudienceElement = () =>
      document.getElementById("report-audience");
    const jsPDFReportAudience = await generatePDF(
      getReportAudienceElement,
      options
    );
    let jsPDFBytesReportAudience = undefined;
    let pdfReportAudience = undefined;
    if (jsPDFReportAudience) {
      jsPDFBytesReportAudience = jsPDFReportAudience.output("arraybuffer");
      pdfReportAudience = await PDFDocument.load(jsPDFBytesReportAudience);
    }

    //report-growth
    const getReportGrowth = () => document.getElementById("report-growth");
    const jsPDFReportGrowth = await generatePDF(getReportGrowth, options);
    let jsPDFBytesReportGrowth = undefined;
    let pdfReportGrowth = undefined;
    if (jsPDFReportGrowth) {
      jsPDFBytesReportGrowth = jsPDFReportGrowth.output("arraybuffer");
      pdfReportGrowth = await PDFDocument.load(jsPDFBytesReportGrowth);
    }

    //report-engagement
    const getReportEngagement = () =>
      document.getElementById("report-engagement");
    const jsPDFReportEngagement = await generatePDF(
      getReportEngagement,
      options
    );
    let jsPDFBytesReportEngagement = undefined;
    let pdfReportEngagement = undefined;
    if (jsPDFReportEngagement) {
      jsPDFBytesReportEngagement = jsPDFReportEngagement.output("arraybuffer");
      pdfReportEngagement = await PDFDocument.load(jsPDFBytesReportEngagement);
    }

    //report-posts
    const getReportPosts = () => document.getElementById("report-posts");
    const jsPDFReportPosts = await generatePDF(getReportPosts, options);
    let jsPDFBytesReportPosts = undefined;
    let pdfReportPosts = undefined;
    if (jsPDFReportPosts) {
      jsPDFBytesReportPosts = jsPDFReportPosts.output("arraybuffer");
      pdfReportPosts = await PDFDocument.load(jsPDFBytesReportPosts);
    }

    //report-word-cloud
    const getReportWordCloud = () =>
      document.getElementById("report-word-cloud");
    const jsPDFReportWordCloud = await generatePDF(getReportWordCloud, options);
    let jsPDFBytesReportWordCloud = undefined;
    let pdfReportWordCloud = undefined;
    if (jsPDFReportWordCloud) {
      jsPDFBytesReportWordCloud = jsPDFReportWordCloud.output("arraybuffer");
      pdfReportWordCloud = await PDFDocument.load(jsPDFBytesReportWordCloud);
    }

    const mergedPdf = await PDFDocument.create();

    // Copia delle pagine da pdf1 a mergedPdf
    const copiedPages1 = await mergedPdf.copyPages(
      coverPdf,
      coverPdf.getPageIndices()
    );
    copiedPages1.forEach((page) => {
      mergedPdf.addPage(page);
    });

    // Copia delle pagine da account-info a mergedPdf
    // const copiedPages2 = await mergedPdf.copyPages(
    //   pdfAccountInfo,
    //   pdfAccountInfo.getPageIndices()
    // );
    // copiedPages2.forEach((page) => {
    //   mergedPdf.addPage(page);
    // });

    // Copia delle pagine da report-standard-analytics a mergedPdf
    if (pdfStandardAnalytics) {
      const copiedPages3 = await mergedPdf.copyPages(
        pdfStandardAnalytics,
        pdfStandardAnalytics.getPageIndices()
      );
      copiedPages3.forEach((page) => {
        mergedPdf.addPage(page);
      });
    }

    // Copia delle pagine da report-audience a mergedPdf
    if (pdfReportAudience) {
      const copiedPages4 = await mergedPdf.copyPages(
        pdfReportAudience,
        pdfReportAudience.getPageIndices()
      );
      copiedPages4.forEach((page) => {
        mergedPdf.addPage(page);
      });
    }

    // Copia delle pagine da report-growth a mergedPdf
    if (pdfReportGrowth) {
      const copiedPages5 = await mergedPdf.copyPages(
        pdfReportGrowth,
        pdfReportGrowth.getPageIndices()
      );
      copiedPages5.forEach((page) => {
        mergedPdf.addPage(page);
      });
    }

    // Copia delle pagine da report-engagement a mergedPdf
    if (pdfReportEngagement) {
      const copiedPages6 = await mergedPdf.copyPages(
        pdfReportEngagement,
        pdfReportEngagement.getPageIndices()
      );
      copiedPages6.forEach((page) => {
        mergedPdf.addPage(page);
      });
    }

    // Copia delle pagine da report-posts a mergedPdf
    if (pdfReportPosts) {
      const copiedPages7 = await mergedPdf.copyPages(
        pdfReportPosts,
        pdfReportPosts.getPageIndices()
      );
      copiedPages7.forEach((page) => {
        mergedPdf.addPage(page);
      });
    }

    // Copia delle pagine da report-word-cloud a mergedPdf
    if (pdfReportWordCloud) {
      const copiedPages8 = await mergedPdf.copyPages(
        pdfReportWordCloud,
        pdfReportWordCloud.getPageIndices()
      );
      copiedPages8.forEach((page) => {
        mergedPdf.addPage(page);
      });
    }

    // PDF unito
    const mergedPdfBytes = await mergedPdf.save();

    if (mergedPdfBytes) {
      const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      downloadBlob(blob, `report-${id}.pdf`);
    }
  };

  const downloadBlob = (blob: Blob, fileName: string) => {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // useEffect(() => {
  //   if (reportError) {
  //     setOpen(true);
  //     resetError();
  //   }
  // }, [reportError]);

  // useEffect(() => {
  //   if (reportDetail) {
  //     const numberOfAge =
  //       reportDetail.followersAudience.age &&
  //       Object.keys(reportDetail.followersAudience.age).length > 0;
  //     const numberOfCountry =
  //       reportDetail.followersAudience.country &&
  //       Object.keys(reportDetail.followersAudience.country).length > 0;
  //     const isGenderRevealed =
  //       reportDetail.followersAudience.gender.F &&
  //       reportDetail.followersAudience.gender.M &&
  //       reportDetail.followersAudience.gender.U;
  //     if (numberOfAge && numberOfCountry && isGenderRevealed) {
  //       setIsAudienceVisible(true);
  //     } else {
  //       setIsAudienceVisible(false);
  //     }
  //   }
  // }, [reportDetail]);

  return (
    <>
      {auth ? (
        <>
          <div>
            <ReportDetailComponent />
          </div>
          <Box padding={"10px"} display={"flex"} justifyContent={"center"}>
            <Button
              variant="contained"
              onClick={() => {
                setOpen(true);
                generatePDFCustom();
              }}
              // disabled={open}
            >
              {t("report.table.download")}
            </Button>
          </Box>
          <Snackbar
            open={open}
            autoHideDuration={30000}
            onClose={() => setOpen(false)}
          >
            <Alert
              onClose={() => setOpen(false)}
              severity="info"
              sx={{ width: "100%" }}
            >
              {t("social-report-detail.download")}
            </Alert>
          </Snackbar>
        </>
      ) : (
        <MetaLogin setAuth={setAuth} onFacebookCallback={saveFacebookInfo} />
      )}
      {/* <InfoModal
        open={open}
        setIsVisible={setOpen}
        goBackText={t("empty_states.no_report.go_back")}
        onDiscardClick={() => setOpen(false)}
      >
        <>
          <Text fontSize={18} fontWeight={700} textAlign={"center"}>
            {t("empty_states.no_report.title")}
          </Text>
          <Text fontSize={15} fontWeight={500} textAlign={"center"}>
            {t("empty_states.no_report.subtitle")}
          </Text>
        </>
      </InfoModal> */}
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    isReportLoading: socialSelectors.isSocialReportLoading(state),
    reportError: socialSelectors.getReportError(state),
  }),
  (dispatch: Dispatch) => ({
    resetError: () => dispatch(socialActions.reportError({ error: "" })),
    saveFacebookInfo: (userInfo: any) =>
      dispatch(facebookUserAsync.saveFacebookInfo(userInfo)),
  })
)(ReportDetail);
